import { createTheme } from '@mui/material/styles';

// Modern brand color palette - refined and optimized for better contrast and visual appeal
const customColors = {
  primary: '#6A4C93',      // Purple - main brand color
  secondary: '#1982C4',    // Blue - secondary brand color
  success: '#8AC926',      // Green - success/positive actions
  warning: '#FFCA3A',      // Yellow - warnings/highlights
  error: '#FF595E',        // Red - errors/urgent actions
  // Modern accent colors for better visual hierarchy
  accent: '#7B68EE',       // Soft purple accent
  accentBlue: '#4A90E2',   // Modern blue accent
  accentGreen: '#6BCB77',  // Softer green for backgrounds
};

// Neutral gray scale for better text hierarchy and subtle backgrounds
export const neutralColors = {
  50: '#FAFAFA',   // Lightest backgrounds
  100: '#F5F5F5',  // Subtle backgrounds
  200: '#EEEEEE',  // Borders and dividers
  300: '#E0E0E0',  // Light dividers
  400: '#BDBDBD',  // Disabled states
  500: '#9E9E9E',  // Placeholder text
  600: '#757575',  // Secondary text
  700: '#616161',  // Secondary text (darker)
  800: '#424242',  // Primary text (lighter)
  900: '#212121',  // Primary text (darkest)
};

// Category-specific colors for better visual organization
export const categoryColors = {
  // Creative & Arts
  creative: '#9C27B0',      // Purple
  arts: '#E91E63',          // Pink
  crafts: '#FF5722',        // Deep Orange
  
  // Learning & Education
  learning: '#2196F3',       // Blue
  workshops: '#00BCD4',      // Cyan
  language: '#0097A7',       // Teal
  
  // Social & Community
  social: '#FF9800',        // Orange
  community: '#FFC107',      // Amber
  
  // Wellness & Health
  wellness: '#4CAF50',       // Green
  fitness: '#8BC34A',        // Light Green
  health: '#66BB6A',         // Medium Green
  
  // Outdoor & Nature
  outdoor: '#388E3C',        // Dark Green
  nature: '#689F38',         // Light Green
  adventure: '#558B2F',      // Forest Green
  
  // Sports & Activities
  sports: '#F44336',         // Red
  winter: '#03A9F4',         // Light Blue
  dance: '#E91E63',          // Pink
  
  // Food & Cooking
  food: '#FF6F00',           // Deep Orange
  cooking: '#FF8F00',        // Amber
  
  // Culture & Events
  culture: '#5E35B1',        // Deep Purple
  events: '#7B1FA2',         // Purple
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: customColors.primary,
      light: '#8B6BB3',
      dark: '#5A3D7A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: customColors.secondary,
      light: '#4A9CD6',
      dark: '#1565A3',
      contrastText: '#FFFFFF',
    },
    success: {
      main: customColors.success,
      light: '#A5D94A',
      dark: '#6B9A1A',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: customColors.warning,
      light: '#FFD666',
      dark: '#E6B800',
      contrastText: '#000000',
    },
    error: {
      main: customColors.error,
      light: '#FF7A7E',
      dark: '#CC464A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: neutralColors[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: neutralColors[900],
      secondary: neutralColors[700], // Improved from #7F8C8D for WCAG AA compliance (4.5:1 ratio)
    },
    grey: {
      50: neutralColors[50],
      100: neutralColors[100],
      200: neutralColors[200],
      300: neutralColors[300],
      400: neutralColors[400],
      500: neutralColors[500],
      600: neutralColors[600],
      700: neutralColors[700],
      800: neutralColors[800],
      900: neutralColors[900],
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: customColors.primary,
    },
    h2: {
      fontWeight: 600,
      color: customColors.primary,
    },
    h3: {
      fontWeight: 600,
      color: customColors.primary,
    },
    h4: {
      fontWeight: 600,
      color: customColors.primary,
    },
    h5: {
      fontWeight: 500,
      color: customColors.primary,
    },
    h6: {
      fontWeight: 500,
      color: customColors.primary,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          // WCAG 2.4.7 - Enhanced focus indicators
          '&:focus-visible': {
            outline: '3px solid #005FCC',
            outlineOffset: '2px',
            boxShadow: '0 0 0 3px rgba(0, 95, 204, 0.3)',
          },
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          // High contrast mode support
          '@media (prefers-contrast: high)': {
            border: '2px solid currentColor',
            '&:focus-visible': {
              outline: '4px solid #000000',
              outlineOffset: '2px',
            },
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          // WCAG 2.3.3 - Respect reduced motion preferences
          '@media (prefers-reduced-motion: reduce)': {
            '&:hover': {
              transform: 'none',
            },
          },
        },
        containedPrimary: {
          backgroundColor: customColors.primary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#5A3D7A', // Darker purple on hover
            boxShadow: '0 6px 16px rgba(106, 76, 147, 0.4)',
          },
        },
        containedSecondary: {
          backgroundColor: customColors.secondary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#1565A3', // Darker blue on hover
            boxShadow: '0 6px 16px rgba(25, 130, 196, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          // WCAG 2.4.7 - Enhanced focus indicators for cards
          '&:focus-within': {
            outline: '3px solid #005FCC',
            outlineOffset: '2px',
          },
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            transform: 'translateY(-4px)',
          },
          // WCAG 2.3.3 - Respect reduced motion preferences
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': {
              transform: 'none',
            },
          },
          // High contrast mode support
          '@media (prefers-contrast: high)': {
            border: '2px solid currentColor',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: customColors.primary,
          color: '#FFFFFF',
        },
        colorSecondary: {
          backgroundColor: customColors.secondary,
          color: '#FFFFFF',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${customColors.primary}, ${customColors.secondary})`,
          boxShadow: `0 4px 20px rgba(106, 76, 147, 0.3)`,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: customColors.primary,
          borderTop: `2px solid ${customColors.secondary}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            color: customColors.warning,
          },
        },
        label: {
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          backgroundColor: customColors.success,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#7AB020',
          },
        },
        secondary: {
          backgroundColor: customColors.warning,
          color: '#000000',
          '&:hover': {
            backgroundColor: '#E6B800',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(106, 76, 147, 0.1)',
        },
        barColorPrimary: {
          backgroundColor: customColors.primary,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: customColors.primary,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default theme; 